/*
 * Sourced from https://v0.dev/chat/menu-navbar-sidebar-w-o-localization-Y0lZuopJCUv
 * See docs/v0.md for more
 */

'use client'

import type React from 'react'
import { useTranslations } from 'next-intl'

import { CheckIcon, ChevronRight, PlusIcon } from 'lucide-react'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarGroup,
    SidebarGroupContent,
    SidebarGroupLabel,
    SidebarHeader,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarRail,
    SidebarSeparator,
} from '@/components/ui/sidebar'
import { useSession } from '@/app/hooks/use-session'
import { trackNavClickEvent } from '@/services/auth/client/auth-events'

import type { NavbarProps } from './navbar'
import { CountryFlag } from './navigation/locale-menu'
import { activeLocaleOption } from './navigation/locale-menu-config'

export const AppSidebar = ({
    menu,
    auth,
    localeOptions,
    ...props
}: Omit<NavbarProps, 'logo'> & React.ComponentProps<typeof Sidebar>) => {
    const t = useTranslations('navigation')
    const { session, logout } = useSession()
    const isLoggedIn = session.isAuthenticated
    const fullName = [session.firstName, session.lastName].filter(Boolean).join(' ')
    // The picker needs both a choice to offer and a current country to sit on the
    // trigger row; `LocaleMenu` gates the navbar copy identically. Resolved once so
    // the JSX below reads as "render it or don't".
    const active = localeOptions && activeLocaleOption(localeOptions)
    const localePicker =
        localeOptions && localeOptions.length > 1 && active
            ? { options: localeOptions, active }
            : undefined

    return (
        <Sidebar collapsible="offcanvas" side="right" {...props}>
            {auth && (
                <SidebarHeader>
                    {/* Account control — mirrors the desktop navbar: logged-out offers
                        sign-up + log-in; logged-in greets by name. Gated on useSession;
                        the offcanvas panel is hidden until opened (post-hydration), so the
                        SSR-logged-out state keeps the HTML user-independent without a flash. */}
                    {isLoggedIn ? (
                        <div className="px-2 py-3 text-base font-semibold">
                            {fullName ? t('greeting', { name: fullName }) : t('myAccount')}
                        </div>
                    ) : (
                        <div className="space-y-2.5 p-2">
                            <Button
                                asChild
                                variant="strong"
                                size="lg"
                                className="w-full font-bold tracking-[2px] uppercase"
                                aria-label={t('signUpAriaLabel')}
                            >
                                <a href={auth.signup.url}>{auth.signup.title}</a>
                            </Button>
                            <Button
                                asChild
                                variant="strong-outline"
                                size="lg"
                                className="w-full font-bold tracking-[2px] uppercase"
                                aria-label={t('logInAriaLabel')}
                            >
                                <a
                                    href={auth.login.url}
                                    onClick={() =>
                                        trackNavClickEvent({
                                            label: 'log_in',
                                            navType: 'hamburger',
                                        })
                                    }
                                >
                                    {auth.login.title}
                                </a>
                            </Button>
                        </div>
                    )}
                </SidebarHeader>
            )}

            <SidebarContent className="gap-0">
                {auth && (
                    <>
                        {/* Account links — rendered in both states (ui-react
                            AccountMenuContent parity); Log Out is logged-in only. */}
                        <SidebarGroup>
                            <SidebarGroupContent>
                                <SidebarMenu>
                                    <SidebarMenuItem>
                                        <SidebarMenuButton asChild>
                                            <a href={auth.myTrips.url}>{auth.myTrips.title}</a>
                                        </SidebarMenuButton>
                                    </SidebarMenuItem>
                                    <SidebarMenuItem>
                                        <SidebarMenuButton asChild>
                                            <a href={auth.account.url}>{auth.account.title}</a>
                                        </SidebarMenuButton>
                                    </SidebarMenuItem>
                                    <SidebarMenuItem>
                                        <SidebarMenuButton asChild>
                                            <a href={auth.points.url}>{auth.points.title}</a>
                                        </SidebarMenuButton>
                                    </SidebarMenuItem>
                                    {isLoggedIn && (
                                        <SidebarMenuItem>
                                            <SidebarMenuButton
                                                onClick={() => {
                                                    trackNavClickEvent({
                                                        label: 'log_out',
                                                        navType: 'hamburger',
                                                    })
                                                    void logout()
                                                }}
                                            >
                                                {t('logOut')}
                                            </SidebarMenuButton>
                                        </SidebarMenuItem>
                                    )}
                                </SidebarMenu>
                            </SidebarGroupContent>
                        </SidebarGroup>

                        {/* Separator */}
                        <div className="px-2">
                            <SidebarSeparator className="mx-auto" />
                        </div>
                    </>
                )}

                {/* Navigation Sections */}
                {menu.map((item) =>
                    item.items && item.items.length > 0 ? (
                        <Collapsible key={item.title} className="group/collapsible">
                            <SidebarGroup>
                                <SidebarGroupLabel
                                    asChild
                                    className="group/label text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground text-sm"
                                >
                                    <CollapsibleTrigger>
                                        {item.title}
                                        <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                                    </CollapsibleTrigger>
                                </SidebarGroupLabel>
                                <CollapsibleContent className="animate-collapsible-down overflow-hidden data-[state=closed]:animate-collapsible-up">
                                    <SidebarGroupContent>
                                        <SidebarMenu>
                                            {item.items.map((subItem) => (
                                                <SidebarMenuItem key={subItem.title}>
                                                    <SidebarMenuButton asChild>
                                                        <a href={subItem.url}>{subItem.title}</a>
                                                    </SidebarMenuButton>
                                                </SidebarMenuItem>
                                            ))}
                                        </SidebarMenu>
                                    </SidebarGroupContent>
                                </CollapsibleContent>
                            </SidebarGroup>
                        </Collapsible>
                    ) : (
                        <SidebarGroup key={item.title}>
                            <SidebarGroupContent>
                                <SidebarMenu>
                                    <SidebarMenuItem>
                                        <SidebarMenuButton asChild>
                                            <a href={item.url}>{item.title}</a>
                                        </SidebarMenuButton>
                                    </SidebarMenuItem>
                                </SidebarMenu>
                            </SidebarGroupContent>
                        </SidebarGroup>
                    ),
                )}

                {/* Country picker — after the site menu, ui-react's offcanvas order.
                    A collapsible group, mirroring ui-react's `VerticalLocaleMenu`: the
                    trigger row IS the active country (flag + name), so a visitor sees
                    where they are without opening anything, and the list stays out of
                    the way of the menu above it. Same primitive as the menu groups
                    beside it — the crawlable copy of these links is the navbar's
                    `<details>`, which ships in the markup at every viewport, so this
                    one is free to mount its content on open like its neighbours. */}
                {localePicker && (
                    <>
                        <div className="px-2">
                            <SidebarSeparator className="mx-auto" />
                        </div>
                        <Collapsible className="group/locale">
                            <SidebarGroup>
                                <SidebarGroupLabel
                                    asChild
                                    // `gap-2` matches SidebarMenuButton's own flag-to-name
                                    // spacing, which SidebarGroupLabel does not set — without
                                    // it the trigger row reads "🇲🇽México" while the rows
                                    // below it are spaced.
                                    className="group/label text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground h-auto gap-2 py-2 text-sm"
                                >
                                    <CollapsibleTrigger
                                        // Purpose AND current country in one name: an
                                        // aria-label replaces name-from-content, so the
                                        // visible country name would otherwise be the
                                        // only thing announced.
                                        aria-label={`${t('chooseCountry')} — ${localePicker.active.countryName}`}
                                    >
                                        <CountryFlag option={localePicker.active} />
                                        {localePicker.active.countryName}
                                        {/* A plus that rotates into an ✕ when open —
                                            ui-react's own affordance for this row
                                            (`listToggleActive`: rotate(45deg)). */}
                                        <PlusIcon
                                            className="ml-auto transition-transform duration-200 group-data-[state=open]/locale:rotate-45"
                                            aria-hidden="true"
                                        />
                                    </CollapsibleTrigger>
                                </SidebarGroupLabel>
                                <CollapsibleContent className="animate-collapsible-down overflow-hidden data-[state=closed]:animate-collapsible-up">
                                    <SidebarGroupContent>
                                        <SidebarMenu>
                                            {localePicker.options.map((option) => (
                                                <SidebarMenuItem key={option.locale}>
                                                    <SidebarMenuButton asChild>
                                                        <a
                                                            href={option.href}
                                                            hrefLang={option.locale}
                                                            aria-current={
                                                                option.isActive ? 'true' : undefined
                                                            }
                                                            /* Indented under the trigger
                                                               row (ui-react: 32px). */
                                                            className={cn(
                                                                'pl-8',
                                                                option.isActive && 'font-semibold',
                                                            )}
                                                        >
                                                            <CountryFlag option={option} />
                                                            {option.countryName}
                                                            {option.isActive && (
                                                                <CheckIcon
                                                                    data-slot="active-country-check"
                                                                    className="ml-auto"
                                                                    aria-hidden="true"
                                                                />
                                                            )}
                                                        </a>
                                                    </SidebarMenuButton>
                                                </SidebarMenuItem>
                                            ))}
                                        </SidebarMenu>
                                    </SidebarGroupContent>
                                </CollapsibleContent>
                            </SidebarGroup>
                        </Collapsible>
                    </>
                )}
            </SidebarContent>

            <SidebarFooter>
                <div className="text-sidebar-foreground/70 p-2 text-center text-xs">
                    © {new Date().getFullYear()} Wanderu
                </div>
            </SidebarFooter>

            <SidebarRail />
        </Sidebar>
    )
}
